import type {
  PolicyCoveragePolicy,
  PolicyCoverageRelationship,
  PolicyCoverageResponse,
} from '../types/api';
import type {
  GraphData,
  GraphEdge,
  GraphNode,
  NodeType,
  PolicyCoverageView,
} from '../types/graph';

type CoverageEntityType = 'user' | 'service_account' | 'resource';

const ROOT_PREFIXES: Record<CoverageEntityType, string> = {
  user: 'user-',
  service_account: 'sa-',
  resource: 'resource-center-',
};

const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase();

const normalizeResourceType = (value: unknown) => {
  const type = normalize(value).replaceAll('_', '-');
  if (type === 'vm' || type === 'virtualmachine') return 'virtual-machine';
  if (type === 'storage' || type === 'bucket' || type === 'storagebucket') return 'storage-bucket';
  return type;
};

const toGraphEntityType = (value: PolicyCoverageRelationship['entityType']): CoverageEntityType => {
  if (value === 'SERVICE_ACCOUNT') return 'service_account';
  if (value === 'RESOURCE') return 'resource';
  return 'user';
};

const isRootNode = (node: GraphNode): node is GraphNode & { type: CoverageEntityType } => {
  const prefix = ROOT_PREFIXES[node.type as CoverageEntityType];
  return !!prefix && node.id.startsWith(prefix) && node.props?.graphId?.startsWith(prefix);
};

const getRootKey = (node: GraphNode & { type: CoverageEntityType }) =>
  node.type === 'resource' && node.props?.coverageEntityId
    ? String(node.props.coverageEntityId)
    : node.id.slice(ROOT_PREFIXES[node.type].length);

const getRootAliases = (node: GraphNode & { type: CoverageEntityType }) => {
  const props = node.props || {};
  return [
    getRootKey(node),
    node.id,
    node.label,
    props.graphId,
    props.email,
    props.userName,
    props.username,
    props.arn,
    props.userId,
    props.azureId,
    props.userPrincipalName,
    props.mail,
    props.displayName,
    props.serviceAccountEmail,
    props.roleArn,
    props.roleName,
    props.principalId,
    props.instanceId,
    props.name,
    props.resourceName,
    props.coverageEntityId,
    props.resourceId,
    props.resourceArn,
    props.instanceArn,
    props.instanceName,
    props.rdsDBInstanceArn,
    props.rdsIdentifierName,
    props.bucketName,
    props.azureVmId,
    props.azureDatabaseId,
    props.databaseName,
    props.azureStorageAccountId,
    props.storageAccountName,
  ]
    .map(normalize)
    .filter(Boolean);
};

const matchesResourceType = (node: GraphNode, relationship: PolicyCoverageRelationship) => {
  if (!relationship.resourceType) return true;
  const relationType = normalizeResourceType(relationship.resourceType);
  const nodeTypes = [
    node.props?.resourceCategory,
    node.props?.resourceType,
  ].map(normalizeResourceType);
  if (nodeTypes.includes(relationType)) return true;
  if (relationType === 'ec2') return nodeTypes.includes('virtual-machine');
  if (relationType === 'rds') return nodeTypes.includes('database');
  if (relationType === 's3') return nodeTypes.includes('storage-bucket');
  return false;
};

const createPolicyNode = (
  policy: PolicyCoveragePolicy,
  coveredGraphIds: string[],
  nodeId = `policy-${encodeURIComponent(policy.policyId)}`,
  graphId?: string,
): GraphNode => ({
  id: nodeId,
  type: 'policy',
  label: policy.title || policy.policyId,
  provider: 'multi',
  props: {
    isPolicyCoverage: true,
    policyId: policy.policyId,
    title: policy.title,
    description: policy.description,
    severity: policy.severity,
    cloudPolicyType: policy.cloudPolicyType,
    policyStatus: policy.policyStatus,
    policyTargetTypes: policy.policyTargetTypes || [],
    isDefault: policy.isDefault,
    linkedAlerts: policy.linkedAlerts || [],
    coveredGraphIds,
    coverageCount: coveredGraphIds.length,
    ...(graphId ? { graphId } : {}),
  },
});

const getPolicy = (
  policyId: string,
  policiesById: Map<string, PolicyCoveragePolicy>,
): PolicyCoveragePolicy => policiesById.get(policyId) || {
  policyId,
  title: policyId,
  description: '',
  severity: '',
  cloudPolicyType: '',
  policyStatus: '',
  policyTargetTypes: [],
  isDefault: false,
  linkedAlerts: [],
};

export const transformPolicyCoverageToGraph = (
  coverage: PolicyCoverageResponse,
  activityNodes: GraphNode[],
  activityEdges: GraphEdge[],
  view: PolicyCoverageView,
): GraphData => {
  const roots = activityNodes.filter(isRootNode);
  const rootIndex = new Map<string, GraphNode[]>();
  const canonicalRootIndex = new Map<string, GraphNode[]>();

  roots.forEach((node) => {
    const canonicalKey = `${node.type}:${normalize(getRootKey(node))}`;
    const canonicalNodes = canonicalRootIndex.get(canonicalKey) || [];
    canonicalNodes.push(node);
    canonicalRootIndex.set(canonicalKey, canonicalNodes);

    getRootAliases(node).forEach((alias) => {
      const key = `${node.type}:${alias}`;
      const indexed = rootIndex.get(key) || [];
      if (!indexed.some((item) => item.id === node.id)) indexed.push(node);
      rootIndex.set(key, indexed);
    });
  });

  const policiesById = new Map(
    (coverage.policies || []).map((policy) => [String(policy.policyId), policy]),
  );
  const relationshipsByPolicy = new Map<string, Array<{ relationship: PolicyCoverageRelationship; node: GraphNode }>>();
  const relationshipKeys = new Set<string>();

  (coverage.relationships || []).forEach((relationship) => {
    const entityType = toGraphEntityType(relationship.entityType);
    const rawId = normalize(relationship.entityId);
    const prefixedId = normalize(`${ROOT_PREFIXES[entityType]}${relationship.entityId}`);
    const canonicalCandidates = canonicalRootIndex.get(`${entityType}:${rawId}`) || [];
    const candidates = canonicalCandidates.length > 0
      ? canonicalCandidates
      : [
        ...(rootIndex.get(`${entityType}:${rawId}`) || []),
        ...(rootIndex.get(`${entityType}:${prefixedId}`) || []),
      ];

    candidates.forEach((node) => {
      if (entityType === 'resource' && !matchesResourceType(node, relationship)) return;
      const key = `${relationship.policyId}:${node.id}`;
      if (relationshipKeys.has(key)) return;
      relationshipKeys.add(key);
      const relations = relationshipsByPolicy.get(String(relationship.policyId)) || [];
      relations.push({ relationship, node });
      relationshipsByPolicy.set(String(relationship.policyId), relations);
    });
  });

  const allPolicyIds = new Set([
    ...policiesById.keys(),
    ...relationshipsByPolicy.keys(),
  ]);
  const policyNodes: GraphNode[] = [];
  const policyEntityNodes: GraphNode[] = [];
  const policyEdges: GraphEdge[] = [];

  allPolicyIds.forEach((policyId) => {
    const relations = relationshipsByPolicy.get(policyId) || [];
    const coveredGraphIds = Array.from(new Set(
      relations.map(({ node }) => String(node.props?.graphId || '')).filter(Boolean),
    ));
    const policy = getPolicy(policyId, policiesById);

    if (view === 'policy') {
      const policyNode = createPolicyNode(policy, coveredGraphIds);
      policyNodes.push(policyNode);

      relations.forEach(({ relationship, node }) => {
        const graphId = String(node.props?.graphId || '');
        const policyEntityNodeId = `${node.id}-policy-${encodeURIComponent(policyId)}`;
        policyEntityNodes.push({
          ...node,
          id: policyEntityNodeId,
          props: {
            ...node.props,
            policyClusterId: policyId,
            policyCoverageSourceNodeId: node.id,
          },
        });
        policyEdges.push({
          id: `policy-coverage-${encodeURIComponent(policyId)}-${encodeURIComponent(node.id)}`,
          source: policyNode.id,
          target: policyEntityNodeId,
          type: 'covered_by_policy',
          props: {
            graphId,
            policyId,
            entityType: relationship.entityType,
            resourceType: relationship.resourceType,
          },
        });
      });
      return;
    }

    relations.forEach(({ relationship, node }) => {
      const graphId = String(node.props?.graphId || '');
      const policyNodeId = `policy-${encodeURIComponent(policyId)}-${encodeURIComponent(graphId)}`;
      policyNodes.push(createPolicyNode(policy, coveredGraphIds, policyNodeId, graphId));
      policyEdges.push({
        id: `policy-coverage-${encodeURIComponent(policyId)}-${encodeURIComponent(node.id)}`,
        source: node.id,
        target: policyNodeId,
        type: 'covered_by_policy',
        props: {
          graphId,
          policyId,
          entityType: relationship.entityType,
          resourceType: relationship.resourceType,
        },
      });
    });
  });

  const nodes = view === 'entity'
    ? [...activityNodes, ...policyNodes]
    : [...policyEntityNodes, ...policyNodes];
  const edges = view === 'entity'
    ? [...activityEdges, ...policyEdges]
    : policyEdges;

  return {
    nodes,
    edges,
    stats: {
      users: roots.filter((node) => node.type === 'user').length,
      service_accounts: roots.filter((node) => node.type === 'service_account').length,
      resources: roots.filter((node) => node.type === 'resource').length,
      edges: edges.length,
    },
  };
};

export const isPolicyCoverageRootType = (type: NodeType): type is CoverageEntityType =>
  type === 'user' || type === 'service_account' || type === 'resource';
